import { useEffect, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import {
  DATE_WITH_TIME_FORMAT,
  MAX_FILTER_ID,
  MIN_FILTER_ID,
  SHORT_DATE_FORMAT,
} from "shared/constants";
import { formatDate } from "shared/locale/format";
import { getAttributeLabel } from "shared/utils";

import { DialogWithTrigger } from "features/components/Dialog";
import { getOperatorToWords } from "features/ui/Filters/constants";
import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import { getTopLevelRowFromFilterGroupState } from "features/ui/Filters/FilterBuilder/utils";
import ExistsFilterLabel from "features/ui/Filters/FilterTypes/ExistsFilter/ExistsFilterLabel";
import { extractTextBetweenSquareBrackets } from "features/ui/Filters/FilterTypes/ExistsFilter/utils";
import OccursFilterLabel from "features/ui/Filters/FilterTypes/OccursFilter/OccursFilterLabel";
import { transformValues } from "features/ui/Filters/transformation-utils";
import {
  FilterDiffData,
  FilterOperator,
  FilterOverviewFormat,
  FilterValue,
} from "features/ui/Filters/types";
import {
  isDateOrDateTimeField,
  isDateTimeField,
  isLastDateFilter,
} from "features/ui/Filters/utils";
import Label from "features/ui/Label";
import { SchemaEntry } from "features/ui/MaterialTable";
import ValuesSearchList from "features/ui/ValuesSearchList";

import { SUPPORTED_DESCRIPTION_TABLE_FIELD_NAMES } from "./FilterDescriptionTable";
import FLabel from "./FLabel";
import { getDateFilterLabel, getLastDateFilterLabel } from "./utils";

interface FilterLabelProps {
  filters: FilterGroupState;
  fieldName: string;
  keyNameMap: { [key: string]: string };
  showValuesIfMultiple?: boolean;
  format?: FilterOverviewFormat;
  noModalForMultipleValues?: boolean;
  baseEntityText?: string;
  tableSchema?: SchemaEntry[];
  dense?: boolean;
  occursBeforeNow?: boolean;
  diff?: FilterDiffData;
}

/**
 * FilterLabel component consist of:
 * - transforming filter values: transforming IDs to names (static transformations & transformations using API).
 * - formatting filter values: returning appropriate UI component to show filter value (badge, label, etc.)
 */
const FilterLabel = ({
  filters,
  fieldName,
  keyNameMap,
  showValuesIfMultiple = false,
  format = "badge",
  noModalForMultipleValues = false,
  baseEntityText,
  occursBeforeNow,
  tableSchema,
  dense,
  diff,
}: FilterLabelProps) => {
  const { t } = useTranslation("filters");
  const childRow = getTopLevelRowFromFilterGroupState(fieldName, filters);

  const [values, setValues] = useState<string[]>(childRow?.values || []);

  const showDescriptionsTable =
    SUPPORTED_DESCRIPTION_TABLE_FIELD_NAMES.includes(fieldName);

  useEffect(() => {
    transformValues(t, filters, fieldName, tableSchema).then(
      (values: string[]) => setValues(values)
    );
  }, [t, fieldName, filters, tableSchema]);

  if (!childRow) return null;

  const numOfValues = values.length;

  const applyValueDiffStyle =
    diff?.changes[diff.level]?.[diff.groupIndex]?.[diff.rowIndex]?.values ??
    false;

  let defaultLabel: FilterValue = (
    <span className={applyValueDiffStyle ? diff?.bgColor : undefined}>
      {values.join(",")}
    </span>
  );

  // Show a modal with description table if there is only one value
  if (showDescriptionsTable && values.length === 1) {
    defaultLabel = (
      <DialogWithTrigger
        triggerElement={
          <Label
            text={values.join(", ")}
            className={classNames(
              "text-blue-400 inline-block cursor-pointer pl-0! mb-0! text-xs!",
              applyValueDiffStyle ? diff?.bgColor : undefined
            )}
          />
        }
        title={getAttributeLabel(fieldName)}
      >
        <ValuesSearchList
          attribute={fieldName}
          title={getAttributeLabel(fieldName)}
          values={values}
          key="values-modal"
          showDescriptionsTable={showDescriptionsTable}
        />
      </DialogWithTrigger>
    );
  }

  const { operator } = childRow;

  let fieldNameFormatted =
    keyNameMap[fieldName] || getAttributeLabel(fieldName);

  const modalTitle = t("filters:values_modal_title", {
    field: fieldNameFormatted,
    operator: getOperatorToWords(t)[operator],
  });

  if (
    !showValuesIfMultiple &&
    numOfValues > 1 &&
    operator !== FilterOperator.BETWEEN
  ) {
    defaultLabel = (noModalForMultipleValues && (
      <span className={applyValueDiffStyle ? diff?.bgColor : undefined}>
        {t("n_values", { num: numOfValues })}
      </span>
    )) || (
      <DialogWithTrigger
        triggerElement={t("n_values", { num: numOfValues })}
        triggerElementClassNames={classNames(
          "text-blue-500 hover:underline",
          applyValueDiffStyle ? diff?.bgColor : undefined
        )}
        title={modalTitle}
      >
        <ValuesSearchList
          attribute={fieldName}
          title={fieldName}
          values={values}
          key="values-modal"
          showDescriptionsTable={showDescriptionsTable}
        />
      </DialogWithTrigger>
    );
  }

  // Joins array values with a space in between so the word-break css property works as it should
  if (
    showValuesIfMultiple &&
    numOfValues > 1 &&
    operator === FilterOperator.IN
  ) {
    defaultLabel = values.join(", ");
  }

  const schemaEntry = tableSchema?.find(
    (entry) => entry.accessor === fieldName
  );

  // Min/max values ("max"/"min") are already localized by transformValues; keep
  // them out of the date-formatting branches so they aren't parsed as dates.
  const isMinMaxSelected =
    !!schemaEntry?.filter?.enableMinMaxFilters &&
    childRow.values.some(
      (value) => value === MAX_FILTER_ID || value === MIN_FILTER_ID
    );

  // format dates: check relative "in last" filters first to avoid passing
  // duration strings like "3m" or "7d" to formatDate
  if (isLastDateFilter(values, operator)) {
    defaultLabel = getLastDateFilterLabel({
      t,
      values,
      applyValueDiffStyle,
      diff,
    });
  } else if (
    !isMinMaxSelected &&
    isDateOrDateTimeField(schemaEntry?.dataType)
  ) {
    defaultLabel = getDateFilterLabel({
      t,
      schemaEntry,
      values,
      operator,
      applyValueDiffStyle,
      diff,
    });
  } else if (
    !isMinMaxSelected &&
    isDateTimeField(schemaEntry?.filter?.filterDataType)
  ) {
    const hasTimeInString = values[0].includes("T");
    defaultLabel = formatDate(
      values[0],
      hasTimeInString ? DATE_WITH_TIME_FORMAT : SHORT_DATE_FORMAT,
      !hasTimeInString
    );
    defaultLabel = (
      <span className={applyValueDiffStyle ? diff?.bgColor : undefined}>
        {defaultLabel}
      </span>
    );
  } else if (numOfValues === 2 && operator === FilterOperator.BETWEEN) {
    defaultLabel = `${values[0] || t("any")} - ${values[1] || t("any")}`;
  }

  if ([FilterOperator.OCCURS, FilterOperator.NOT_OCCURS].includes(operator)) {
    if (format === "label-inline") {
      defaultLabel = (
        <OccursFilterLabel
          operator={operator}
          value={values[0]}
          format={format}
          baseEntityText={baseEntityText}
          occursBeforeNow={occursBeforeNow}
          diff={diff}
          fieldName={fieldName}
        />
      );
    } else {
      defaultLabel = "";
    }
  }

  if (
    [FilterOperator.EXISTS, FilterOperator.NOT_EXISTS].includes(operator) &&
    numOfValues === 1
  ) {
    if (format === "label-inline") {
      const innerFilter = extractTextBetweenSquareBrackets(values[0]);
      defaultLabel = (
        <ExistsFilterLabel
          value={innerFilter || ""}
          tableSchema={tableSchema}
          diff={diff}
          fieldName={fieldName}
        />
      );
    } else {
      defaultLabel = "";
    }
  }

  return (
    <FLabel
      fieldName={fieldNameFormatted}
      operator={operator}
      fieldValue={defaultLabel}
      format={format}
      dense={dense}
      dataType={schemaEntry?.filter?.filterDataType}
      diff={diff}
    />
  );
};

export default FilterLabel;
